import { useState, type ReactNode } from 'react'
import { ChevronRight, Eye } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { helpPath } from '@/features/help/_shared/paths'
import { stamDisplay, injuryDisplay } from '@/features/_shared/condition'
import type { PlayerCardData, RatingSet } from '../types'
import { PositionChips } from '@/features/player/view/PositionChips'
import {
  RatingCell,
  ratingColorForValue,
  sectionTint,
  type RatingSection,
} from '../../../_shared/ratings'
import { cardTheadRow } from '../cardTableStyles'

// Column descriptor — key into RatingSet, display label, section tint
interface Col {
  key: keyof RatingSet
  label: ReactNode
  section: RatingSection
}

// OVR with a per-kind subscript (OVRb / OVRp) — matches the roster / Free
// Agency / players / draft-list convention, where each section's overall leads
// the ratings that make it up.
const ovrLabel = (suffix: 'b' | 'p'): ReactNode => (
  <span>OVR<sub><i>{suffix}</i></sub></span>
)

const OVR_COL: Col = { key: 'overall', label: 'OVR', section: 'overalls' }

const PITCHER_COLS: Col[] = [
  { key: 'overallPitching', label: ovrLabel('p'), section: 'pitching' },
  { key: 'po', label: 'PO', section: 'pitching' },
  { key: 'fi', label: 'FI', section: 'pitching' },
  { key: 'co', label: 'CO', section: 'pitching' },
  { key: 'sy', label: 'SY', section: 'pitching' },
]

const BATTER_COLS: Col[] = [
  { key: 'overallHitting', label: ovrLabel('b'), section: 'batting' },
  { key: 'di', label: 'DI', section: 'batting' },
  { key: 'cn', label: 'CN', section: 'batting' },
  { key: 'ba', label: 'BA', section: 'batting' },
  { key: 'sl', label: 'SL', section: 'batting' },
  { key: 'sp', label: 'SP', section: 'batting' },
]

// HR (Hold Runner) is a FIELDING rating and leads the block, but only for
// pitchers: dbo.PlayerOverallFielding weights it into OVRf (simModelWeights.hr
// = 1 at posn 1, 0 everywhere else), while PlayerOverallPitching never sees it.
const HOLD_RUNNER_COL: Col = { key: 'hr', label: 'HR', section: 'fielding' }

const FIELDING_COLS: Col[] = [
  { key: 'ra', label: 'RA', section: 'fielding' },
  { key: 'gl', label: 'GL', section: 'fielding' },
  { key: 'ar', label: 'AR', section: 'fielding' },
]

const EN_COL: Col = { key: 'en', label: 'EN', section: 'endurance' }

function buildColumns(isPitcher: boolean): Col[] {
  return [
    OVR_COL,
    ...(isPitcher ? PITCHER_COLS : BATTER_COLS),
    ...(isPitcher ? [HOLD_RUNNER_COL, ...FIELDING_COLS] : FIELDING_COLS),
    EN_COL,
  ]
}

// Actual = roster-style stacked cell (actual on top, potential smaller below).
// The chevron expands the actuals' history as single lines beneath it, so the
// actuals read as one vertical list (Actual → prior years, newest first).
// The section title lives in the table's corner header cell (no standalone
// title row) — the Summary is a cheat-sheet, chrome is kept to a minimum.
export function RatingsSection({ player }: { player: PlayerCardData; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(false)
  // Multi-Position card: same selector as the full player page, so the two
  // surfaces behave identically. Silent below two carded positions.
  const positionRatings = (player.positionRatings ?? []).length >= 2 ? (player.positionRatings ?? []) : []
  const [selectedDposn, setSelectedDposn] = useState<number | null>(null)
  const selectedPosition = positionRatings.length
    ? (positionRatings.find((p) => p.dposn === selectedDposn) ?? positionRatings.find((p) => p.isPrimary))
    : undefined
  // Only what a position change actually moves is substituted; the hitting family
  // and endurance are the player's wherever he stands.
  const atPosition = (key: string) =>
    selectedPosition
      ? (key === 'overall' ? selectedPosition.overall
        : key === 'overallFielding' ? selectedPosition.overallFielding
        : key === 'ra' ? selectedPosition.ra
        : key === 'gl' ? selectedPosition.gl
        : undefined)
      : undefined
  const { attrs, pitches } = player.ratings
  // The "Actual" row already represents the current season, so drop the
  // history entry for that same season — otherwise the current year shows twice.
  const currentLabel = player.stats.currentSeason?.label
  // Newest-first below the current row (compact recent-ratings trend). Sort by
  // numeric age, NOT the `asOf` string — a lexicographic sort ranks yearless
  // "Age N" labels above real year strings and pinned the earliest season on top.
  const history = [...(player.ratingsHistory ?? [])]
    .filter((h) => h.asOf !== currentLabel)
    .sort((a, b) => (b.age ?? Number.NEGATIVE_INFINITY) - (a.age ?? Number.NEGATIVE_INFINITY))
  const columns = buildColumns(player.isPitcher)
  const hasHistory = history.length > 0

  // Live condition (#779): current stamina — Round(cRatingEn / ratingEnRaw * 100)
  // — mirrors the lineup pages' Stam column so the tank level reads the same
  // everywhere. Season fatigue rides alongside but only when non-zero. Both are
  // hidden entirely when there's no live endurance (retired / missing data).
  const cond = player.ratings.condition
  const stam = stamDisplay(cond?.cRatingEn, cond?.ratingEnRaw)
  const sf = injuryDisplay(cond?.seasonFatigue)
  const showCondition = stam !== '—'

  return (
    <section className="border-b border-slate-200 pt-2 pb-3">
      {positionRatings.length > 0 && (
        <div className="pb-2">
          <PositionChips
            rows={positionRatings}
            selectedDposn={selectedPosition?.dposn}
            onSelect={setSelectedDposn}
          />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-max text-sm tabular-nums">
          <thead>
            <tr className={cardTheadRow}>
              <th className="py-1 pr-3 text-left font-semibold">
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => hasHistory && setOpen((o) => !o)}
                    aria-expanded={open}
                    className={cn(
                      'group -ml-1 flex items-center gap-1 rounded px-1 text-left transition-colors',
                      hasHistory ? 'hover:bg-slate-100' : 'cursor-default',
                    )}
                  >
                    <ChevronRight
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform group-hover:text-navy',
                        open && 'rotate-90',
                        !hasHistory && 'opacity-0',
                      )}
                    />
                    <span className="text-xs font-bold uppercase tracking-wide text-navy">Ratings</span>
                  </button>
                  {/* Scouting badge (#537): when the league has scouting on the
                      displayed ratings are estimates. The pill marks it; the "?"
                      opens a click popover (no hover dependency, so it works on
                      touch) explaining it, with a link to the Scouting guide. */}
                  {player.ratings.scoutingOn && (
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 ring-1 ring-inset ring-amber-300/70">
                        <Eye className="h-3 w-3" aria-hidden="true" /> Scouting on
                      </span>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label="What does scouting on mean?"
                            className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-200 text-[10px] font-bold leading-none text-amber-800 transition-colors hover:bg-amber-300 focus:outline-none focus:ring-1 focus:ring-amber-400"
                          >
                            ?
                          </button>
                        </PopoverTrigger>
                        <PopoverContent side="top" align="start" className="w-64 text-sm">
                          The ratings shown here are estimates &mdash; a player&apos;s actual
                          ratings may differ from what you see.
                          <div className="mt-2">
                            <a href={helpPath('scouting')} className="font-semibold text-blue hover:underline">
                              Read more about scouting
                            </a>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </span>
                  )}
                </div>
              </th>
              {columns.map((col) => (
                <th key={col.key} className={`px-2 py-1 text-center font-semibold ${sectionTint(col.section, col.key)}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Current ratings — actual stacked over a smaller, muted potential.
                The row label stacks "Actual / Potential" the same way so both
                numbers are labeled (was "Actual" only, leaving the potential
                value beneath it unlabeled). */}
            <tr>
              <td className="py-1 pr-3 text-left">
                <div className="flex flex-col leading-tight">
                  <span className="text-sm font-semibold text-slate-600">Actual</span>
                  <span className="text-[13px] text-slate-400">Potential</span>
                </div>
              </td>
              {columns.map((col) => {
                const swapped = atPosition(col.key)
                return (
                  <td key={col.key} className={`px-2 py-1 text-center ${sectionTint(col.section, col.key)}`}>
                    <span
                      key={selectedPosition?.dposn ?? 'base'}
                      className={swapped ? 'block animate-rating-flash' : undefined}
                    >
                      <RatingCell
                        rating={swapped ?? attrs[col.key] ?? undefined}
                        showPotential
                      />
                    </span>
                  </td>
                )
              })}
            </tr>
            {/* Prior-year actuals — single lines, continuing the actuals list. */}
            {open &&
              history.map((h, i) => (
                <tr key={h.asOf} className={cn(i === 0 && 'border-t border-slate-100')}>
                  <td className="py-0.5 pr-3 text-left text-xs text-slate-500">{h.asOf}</td>
                  {columns.map((col) => {
                    const r = h.attrs[col.key]
                    return (
                      <td key={col.key} className={`px-2 py-0.5 text-center ${sectionTint(col.section, col.key)}`}>
                        <span className={cn('font-semibold', ratingColorForValue(r?.current))}>{r?.current ?? '—'}</span>
                      </td>
                    )
                  })}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {showCondition && (
        <p className="mt-2.5 text-right text-sm text-slate-600">
          <span className="font-medium text-slate-500">Stamina: </span>
          <span className="font-semibold tabular-nums text-slate-700">{stam}</span>
          {sf !== '—' && (
            <>
              <span className="mx-1.5 text-slate-300">·</span>
              <span className="font-medium text-slate-500">Season fatigue: </span>
              <span className="font-semibold tabular-nums text-slate-700">{sf}</span>
            </>
          )}
        </p>
      )}

      {pitches && pitches.length > 0 && (
        <p className="mt-2.5 text-sm text-slate-600">
          <span className="font-medium text-slate-500">Pitches: </span>
          {pitches.join(', ')}
        </p>
      )}
    </section>
  )
}
